import { useTranslation } from 'react-i18next'

import { cancelConsultation } from '@/api/consultations'
import prescriptionIcon from '@/assets/icons/consultations/prescription.svg'
import assessmentIcon from '@/assets/icons/consultations/professional-assessment.svg'
import medicalIcon from '@/assets/icons/consultations/text.svg'
import videoIcon from '@/assets/icons/consultations/video.svg'
import voiceIcon from '@/assets/icons/consultations/voice.svg'
import { BackButton } from '@/components/BackButton'
import { GeneralErrorsBanner } from '@/components/GeneralErrorsBanner'
import { Close } from '@/components/icons/Close'
import { useConsultationStep } from '@/hooks/useConsultationStep'
import { useConsultationType } from '@/hooks/useConsultationType'
import { useLoading } from '@/hooks/useLoading'
import { useUser } from '@/hooks/useUser'
import { CONSULTATION_STEPS, CONSULTATION_TYPE } from '@/types/consultation'

const AVAILABLE_CONSULTATION_TYPES: CONSULTATION_TYPE[] = [
  CONSULTATION_TYPE.MEDICAL,
  CONSULTATION_TYPE.VIDEO,
  CONSULTATION_TYPE.VOICE,
  CONSULTATION_TYPE.PRESCRIPTION,
  CONSULTATION_TYPE.ASSESSMENT
]

const CONSULTATION_ICONS: Record<CONSULTATION_TYPE, string> = {
  [CONSULTATION_TYPE.MEDICAL]: medicalIcon,
  [CONSULTATION_TYPE.VIDEO]: videoIcon,
  [CONSULTATION_TYPE.VOICE]: voiceIcon,
  [CONSULTATION_TYPE.PRESCRIPTION]: prescriptionIcon,
  [CONSULTATION_TYPE.ASSESSMENT]: assessmentIcon
}

export const TabBar = () => {
  const { t } = useTranslation()
  const { consultationType, updateConsultationType } = useConsultationType()
  const { currentConsultationStep } = useConsultationStep()
  const { showLoading, hideLoading, loading } = useLoading()
  const { user } = useUser()
  const { updateStep } = useConsultationStep()

  const handleCancelConsultation = () => {
    showLoading()

    cancelConsultation({
      uniqueId: user?.data?.uniqueId as string
    })
      .then(() => {
        updateStep(CONSULTATION_STEPS.LANDING)
      })
      .finally(() => {
        hideLoading()
      })
  }

  const types = Object.values(CONSULTATION_TYPE).filter((type) => AVAILABLE_CONSULTATION_TYPES.includes(type))
  const onlyOneType = types.length === 1

  return (
    <div className="sticky top-0 z-20 flex w-full flex-col shadow-soft">
      <header className="flex flex-col gap-4 bg-primary bg-header-pattern bg-cover bg-no-repeat py-4 px-6">
        {currentConsultationStep === CONSULTATION_STEPS.LANDING ? (
          <>
            <div className={`flex h-9 w-full items-center ${onlyOneType ? 'ml-4 gap-8' : 'justify-around gap-2'}`}>
              {types.map((type) => (
                <button key={type} onClick={() => updateConsultationType(type)} className="flex w-9 items-center justify-center">
                  <img
                    className={`transition-all ${consultationType === type ? 'h-7' : 'h-5 opacity-80'}`}
                    src={CONSULTATION_ICONS[type]}
                    alt={type}
                  />
                </button>
              ))}

              {onlyOneType && <h2 className="text-xl font-bold text-white">{t(`general.consultation.${consultationType}.title`)}</h2>}
            </div>

            <div className="flex animate-fade-in flex-col gap-1 px-2" key={consultationType}>
              {!onlyOneType && <h2 className="text-xl font-bold text-white">{t(`general.consultation.${consultationType}.title`)}</h2>}
              <p className="text-sm text-white">{t(`general.consultation.${consultationType}.description`)}</p>
            </div>
          </>
        ) : (
          <div className="flex animate-fade-in items-center">
            {currentConsultationStep === CONSULTATION_STEPS.FORM ? (
              <BackButton />
            ) : (
              <img className={`h-7 transition-all`} src={CONSULTATION_ICONS[consultationType]} alt={consultationType} />
            )}

            <h2 className="grow text-center text-xl font-bold text-white">{t(`general.consultation.${consultationType}.title`)}</h2>

            {[CONSULTATION_STEPS.MATCHING, CONSULTATION_STEPS.MATCH].includes(currentConsultationStep as CONSULTATION_STEPS) && (
              <button onClick={handleCancelConsultation} className="disabled:opacity-50" disabled={loading}>
                <Close customClassName="text-white" />
              </button>
            )}
          </div>
        )}
      </header>

      <GeneralErrorsBanner />
    </div>
  )
}
